// pipelineCardSequences — Vuex module that caches active sequence progress
// by card ID. This enables the PipelineCard badge to read sequence data
// reactively without triggering N+1 API requests on board mount.
//
// Population strategy (MVP):
//   - CardSequencesSection already fetches sequences when a card detail is
//     opened. After fetch, it commits SET_CARD_SEQUENCES here.
//   - PipelineCard reads `activeSequenceByCardId[card.id]` from this store.
//   - Cold-start: badge appears only after the user has opened card details
//     once (or after a background refresh). This is acceptable for Phase 1.2.

import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import { contaDaTela } from 'dashboard/routes/enderecos/conta';

// Helper: read accountId from the URL. SPA routes guarantee /app/accounts/:id/...
// We avoid coupling to vue-router here so the store stays usable from anywhere.
const currentAccountIdFromRoute = () => {
  // Endereço antigo ou limpo (06/10/2026) — `routes/enderecos/conta.js`.
  const parsed = Number(contaDaTela());
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const state = {
  // Map of cardId → first active sequence summary (or null)
  // { [cardId]: { id, name, current_step, total_steps, progress_percentage, status } | null }
  activeByCardId: {},
  // Track in-flight fetches to avoid duplicate requests
  fetchingCardIds: new Set(),
  // Audit 2026-05-07 — invalidate cache when the user switches between
  // accounts. Without this, cards loaded for account A linger in memory
  // and PipelineCard.vue mounted briefly during a route transition fires
  // /accounts/B/.../cards/<cardA>/sequences → 404 storms (backend protects
  // correctly, but the UX is noisy and confusing).
  lastAccountId: null,
};

const getters = {
  getActiveSequenceForCard: _state => cardId =>
    _state.activeByCardId[cardId] ?? null,
};

const actions = {
  // Called by CardSequencesSection after it fetches sequences so the kanban
  // badge can read the data without its own API call.
  setCardSequences({ commit }, { cardId, sequences }) {
    const active = (sequences || []).find(
      s => s.status === 'active' || s.status === 'paused'
    );
    commit('SET_CARD_ACTIVE_SEQUENCE', {
      cardId,
      sequence: active
        ? {
            id: active.id,
            name: active.definition?.name || '',
            current_step: active.current_step,
            total_steps: active.total_steps,
            progress_percentage: active.progress_percentage,
            status: active.status,
          }
        : null,
    });
  },

  // On-demand fetch for a single card — called by PipelineCard on mount
  // if the card is not yet in cache. Guards against duplicate in-flight requests.
  async fetchForCard({ commit, state: _state }, cardId) {
    // Snapshot the accountId at dispatch time. If the route has changed by
    // the time the response arrives, drop the result (defensive — covers
    // browser back/forward + race conditions during route transition).
    const requestAccountId = currentAccountIdFromRoute();

    // Cache invalidation: if user switched accounts, drop everything before
    // honoring any cached value.
    if (
      requestAccountId != null &&
      _state.lastAccountId != null &&
      _state.lastAccountId !== requestAccountId
    ) {
      commit('RESET_FOR_ACCOUNT_SWITCH', requestAccountId);
    } else if (_state.lastAccountId == null && requestAccountId != null) {
      commit('SET_LAST_ACCOUNT_ID', requestAccountId);
    }

    if (
      cardId == null ||
      _state.fetchingCardIds.has(cardId) ||
      cardId in _state.activeByCardId
    ) {
      return;
    }
    commit('SET_FETCHING', { cardId, value: true });
    try {
      const { data } = await PipelineCardsAPI.getCardSequences(cardId);
      // Drop response if route changed mid-flight (defensive — A4).
      const responseAccountId = currentAccountIdFromRoute();
      if (responseAccountId !== requestAccountId) {
        return;
      }
      const sequences = data.data || [];
      const active = sequences.find(
        s => s.status === 'active' || s.status === 'paused'
      );
      commit('SET_CARD_ACTIVE_SEQUENCE', {
        cardId,
        sequence: active
          ? {
              id: active.id,
              name: active.definition?.name || '',
              current_step: active.current_step,
              total_steps: active.total_steps,
              progress_percentage: active.progress_percentage,
              status: active.status,
            }
          : null,
      });
    } catch {
      // Silently ignore — badge just won't appear for this card
      commit('SET_CARD_ACTIVE_SEQUENCE', { cardId, sequence: null });
    } finally {
      commit('SET_FETCHING', { cardId, value: false });
    }
  },

  // Clear a single card's cache entry (e.g., after completing a step)
  clearForCard({ commit }, cardId) {
    commit('CLEAR_CARD', cardId);
  },

  // Wipe the entire cache. Called by App.vue when accountId changes.
  clearAll({ commit }) {
    commit('RESET_FOR_ACCOUNT_SWITCH', currentAccountIdFromRoute());
  },
};

const mutations = {
  SET_CARD_ACTIVE_SEQUENCE(_state, { cardId, sequence }) {
    _state.activeByCardId = { ..._state.activeByCardId, [cardId]: sequence };
  },
  SET_FETCHING(_state, { cardId, value }) {
    const next = new Set(_state.fetchingCardIds);
    if (value) next.add(cardId);
    else next.delete(cardId);
    _state.fetchingCardIds = next;
  },
  CLEAR_CARD(_state, cardId) {
    const next = { ..._state.activeByCardId };
    delete next[cardId];
    _state.activeByCardId = next;
  },
  RESET_FOR_ACCOUNT_SWITCH(_state, newAccountId) {
    _state.activeByCardId = {};
    _state.fetchingCardIds = new Set();
    _state.lastAccountId = newAccountId;
  },
  SET_LAST_ACCOUNT_ID(_state, accountId) {
    _state.lastAccountId = accountId;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
